/**
 * A conta de pan/zoom de uma tela que desenha por `transform`.
 *
 * Existe fora do componente por ser pura e por ser o mesmo cálculo em dois gestos que
 * pareciam diferentes: a roda do mouse mantém fixo o ponto sob o cursor, e a pinça mantém
 * sob os dedos o ponto que estava lá quando o gesto começou. É a mesma operação — âncora
 * antes, âncora depois, escala nova —, só que na roda as duas âncoras coincidem.
 */

export type Transform = { scale: number; tx: number; ty: number }
export type ViewportPoint = { x: number; y: number }

export function clampScale(scale: number, min: number, max: number): number {
    return Math.min(Math.max(scale, min), max)
}

/**
 * O deslocamento que leva o ponto do desenho que estava sob `before` para debaixo de
 * `after`, na escala nova.
 *
 * `from` é o estado de referência, e não necessariamente o atual: na pinça ele é o do
 * início do gesto. Medir a partir do quadro anterior acumularia arredondamento a cada
 * evento, e o desenho escorregaria sob os dedos.
 */
export function anchoredTransform(
    from: Transform, scale: number, before: ViewportPoint, after: ViewportPoint
): Transform {
    const worldX = (before.x - from.tx) / from.scale
    const worldY = (before.y - from.ty) / from.scale
    return {scale, tx: after.x - worldX * scale, ty: after.y - worldY * scale}
}

export type Size = { width: number; height: number }

/**
 * O deslocamento de um eixo, preso ao que faz sentido ver.
 *
 * Maior que a janela, anda — mas não além das bordas, para não sobrar vazio de um lado
 * com conteúdo escondido do outro. Cabendo inteiro, anda dentro do espaço livre, sem
 * nunca sair da janela.
 *
 * A primeira versão CENTRALIZAVA e travava o eixo quando o desenho cabia, para um
 * diagrama pequeno não passear por um vão vazio. A dose estava errada: em vez de limitar
 * o quanto anda, proibia — e um eixo morto no arrasto parece defeito, não regra. Preso à
 * janela, o desenho continua sem nunca sumir de vista.
 */
function boundedOffset(offset: number, content: number, view: number): number {
    const free = view - content
    if (content <= view) return Math.min(Math.max(offset, 0), free)
    return Math.min(Math.max(offset, free), 0)
}

/**
 * Prende o desenho à janela. Sem medida da janela não há o que prender — é o caso do
 * primeiro quadro, antes de a viewport ter tamanho.
 */
export function boundedTransform(next: Transform, content: Size, view: Size): Transform {
    if (!view.width || !view.height) return next
    return {
        scale: next.scale,
        tx: boundedOffset(next.tx, content.width * next.scale, view.width),
        ty: boundedOffset(next.ty, content.height * next.scale, view.height)
    }
}
